import {
  Home,
  Search,
  MessageSquare,
  ClipboardList,
  User as UserIcon,
  Bell,
  Heart,
  Star,
  Calendar,
  BarChart3,
  FolderKanban,
  Lock,
  Ban,
  Briefcase,
  Pencil,
  Settings,
  Frown,
  Cat,
  Image as ImageIcon,
  Info,
  Send,
  Plus,
  X,
  FlaskConical,
  Stethoscope,
  Car,
  Laptop,
  PartyPopper,
  Sparkles,
  BookOpen,
  Scale,
  Banknote,
  Baby,
  Brain,
  Scissors,
  Dumbbell,
  Music,
  Languages,
  Calculator,
  Package,
  Palette,
  PenTool,
  Camera,
  Gem,
  ChefHat,
  Flower,
  Building2,
  Wrench,
  Brush,
  Truck,
  PawPrint,
  GraduationCap,
  HeartPulse,
  MoreHorizontal,
  UserX,
  Archive,
  ArchiveRestore,
  Minus,
  Trash2,
  FileText,
  ShoppingCart,
  Globe,
  MapPin,
} from 'lucide-react';
import styles from './Icon.module.css';

/**
 * Shared SVG icon component (F-140). Wraps `lucide-react` so the rest of the
 * app talks in stable string names. Use semantic names so we can swap icon
 * library later without touching call sites.
 *
 * R2 constitution: no emoji as iconography. Expressive emoji in bot voice is
 * still allowed (see `bot/copy.py` cat-family whitelist).
 */
const MAP = {
  home: Home,
  search: Search,
  chats: MessageSquare,
  orders: ClipboardList,
  me: UserIcon,
  bell: Bell,
  favorite: Heart,
  saved: Star,
  calendar: Calendar,
  stats: BarChart3,
  crm: FolderKanban,
  verify: Lock,
  blocked: Ban,
  presets: Briefcase,
  edit: Pencil,
  settings: Settings,
  sad: Frown,
  cat: Cat,
  image: ImageIcon,
  info: Info,
  send: Send,
  plus: Plus,
  x: X,
  flask: FlaskConical,
  // F-160 (C2): category icon set — Lucide replacements for category emoji.
  stethoscope: Stethoscope,
  car: Car,
  laptop: Laptop,
  'party-popper': PartyPopper,
  sparkles: Sparkles,
  'book-open': BookOpen,
  scale: Scale,
  banknote: Banknote,
  baby: Baby,
  brain: Brain,
  scissors: Scissors,
  dumbbell: Dumbbell,
  music: Music,
  languages: Languages,
  calculator: Calculator,
  package: Package,
  palette: Palette,
  'pen-tool': PenTool,
  camera: Camera,
  gem: Gem,
  'chef-hat': ChefHat,
  flower: Flower,
  building: Building2,
  wrench: Wrench,
  brush: Brush,
  truck: Truck,
  'paw-print': PawPrint,
  'graduation-cap': GraduationCap,
  'heart-pulse': HeartPulse,
  'more-horizontal': MoreHorizontal,
  // F-160 (B4): anonymity badge on Reverse Board.
  'user-x': UserX,
  // F-170 B1.7: per-row chat archive / unarchive actions.
  archive: Archive,
  'archive-restore': ArchiveRestore,
  // F-170 B4: cart + review affordances.
  minus: Minus,
  trash: Trash2,
  'file-text': FileText,
  star: Star,
  'shopping-cart': ShoppingCart,
  // F-170 B3.2: per-preset location chips (online / delivery / onsite).
  globe: Globe,
  'map-pin': MapPin,
} as const;

export type IconName = keyof typeof MAP;

export interface IconProps {
  name: IconName;
  size?: number;
  strokeWidth?: number;
  className?: string;
  spin?: boolean;
  /** Fill colour for the SVG (e.g. "currentColor"). Default: no fill. */
  fill?: string;
  /** Stroke colour override. Default: inherits via `currentColor`. */
  color?: string;
  'aria-label'?: string;
}

export function Icon({
  name,
  size = 24,
  strokeWidth = 2,
  className,
  spin = false,
  fill,
  color,
  ...rest
}: IconProps): JSX.Element {
  const LucideIcon = MAP[name];
  const ariaLabel = rest['aria-label'];
  const classes = [className, spin ? styles.spin : null].filter(Boolean).join(' ');
  return (
    <LucideIcon
      size={size}
      strokeWidth={strokeWidth}
      className={classes || undefined}
      aria-hidden={!ariaLabel}
      aria-label={ariaLabel}
      fill={fill}
      color={color}
    />
  );
}
